import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useJobMessages, JobMessage } from '@/hooks/useJobMessages';
import { useAuth } from '@/lib/auth';
import { formatDistanceToNow, format, isToday, isYesterday } from 'date-fns';
import { Send, Edit2, Check, X, AlertTriangle, Info } from 'lucide-react';
import { toast } from 'sonner';

interface MessagesTabProps {
  jobId: string;
  jobStatus: string;
  organizationId: string;
}

export const MessagesTab = ({ jobId, jobStatus, organizationId }: MessagesTabProps) => {
  const { user } = useAuth();
  const { 
    messages, 
    unreadCount, 
    loading, 
    error, 
    validateMessage,
    sendMessage, 
    sendFlaggedMessage,
    editMessage,
    editFlaggedMessage, 
    markAsRead 
  } = useJobMessages(jobId);
  
  const [newMessage, setNewMessage] = useState('');
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [sending, setSending] = useState(false);
  
  // Contact info warning dialog state
  const [showContactWarningDialog, setShowContactWarningDialog] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [pendingEditId, setPendingEditId] = useState<string | null>(null);
  const [detectedContactInfo, setDetectedContactInfo] = useState<{type: string; content: string} | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Check if messaging is available (job must be awarded or later)
  const isMessagingAvailable = ['awarded', 'in_progress', 'completed_pending_review', 'accepted', 'rejected', 'disputed', 'closed'].includes(jobStatus);
  
  // Check if messaging is read-only (job completed/archived)
  const isReadOnly = ['accepted', 'rejected', 'disputed', 'closed'].includes(jobStatus);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Mark messages as read when component mounts and when new messages arrive
  useEffect(() => {
    if (messages.length > 0 && unreadCount > 0) {
      markAsRead();
    }
  }, [messages.length, unreadCount, markAsRead]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending) return;

    // Validate message content for contact information
    const validation = validateMessage(newMessage);
    
    if (!validation.isValid) {
      // Show warning dialog instead of blocking
      setPendingMessage(newMessage);
      setPendingEditId(null);
      setDetectedContactInfo({
        type: validation.detectedType || 'contact_info',
        content: validation.flaggedContent || ''
      });
      setShowContactWarningDialog(true);
      return;
    }

    // No contact info detected, send normally
    await sendMessageDirectly(newMessage);
  };

  const sendMessageDirectly = async (content: string) => {
    try {
      setSending(true);
      const result = await sendMessage(content, organizationId);
      
      if (result?.success === false) {
        toast.error(result.error || 'Failed to send message');
      } else {
        setNewMessage('');
        toast.success('Message sent');
      }
    } catch (err) {
      toast.error('Failed to send message: ' + (err as Error).message);
    } finally {
      setSending(false);
    }
  };

  const handleSendAnyway = async () => {
    if (!pendingMessage || !detectedContactInfo) return;

    setShowContactWarningDialog(false);
    
    try {
      setSending(true);
      
      if (pendingEditId) {
        // This is an edit
        const result = await editFlaggedMessage(
          pendingEditId,
          pendingMessage,
          organizationId,
          detectedContactInfo.type,
          detectedContactInfo.content
        );
        
        if (result?.success === false) {
          toast.error(result.error || 'Failed to update message');
        } else {
          setEditingMessageId(null);
          setEditingContent('');
          toast.success('Message updated');
        }
      } else {
        // This is a new message
        const result = await sendFlaggedMessage(
          pendingMessage,
          organizationId,
          detectedContactInfo.type,
          detectedContactInfo.content
        );
        
        if (result?.success === false) {
          toast.error(result.error || 'Failed to send message');
        } else {
          setNewMessage('');
          toast.success('Message sent');
        }
      }
    } catch (err) {
      toast.error('Failed to send message: ' + (err as Error).message);
    } finally {
      setSending(false);
      setPendingMessage(null);
      setPendingEditId(null);
      setDetectedContactInfo(null);
    }
  };

  const handleEditMessage = () => {
    setShowContactWarningDialog(false);
    // Keep the message content so user can edit it
    // Don't clear pendingMessage as user may want to modify it
    setPendingMessage(null);
    setPendingEditId(null);
    setDetectedContactInfo(null);
  };

  const handleEditStart = (message: JobMessage) => {
    setEditingMessageId(message.id);
    setEditingContent(message.message_content);
  };

  const handleEditSave = async () => {
    if (!editingMessageId || !editingContent.trim()) return;

    // Validate edited content for contact information
    const validation = validateMessage(editingContent);
    
    if (!validation.isValid) {
      // Show warning dialog instead of blocking
      setPendingMessage(editingContent);
      setPendingEditId(editingMessageId);
      setDetectedContactInfo({
        type: validation.detectedType || 'contact_info',
        content: validation.flaggedContent || ''
      });
      setShowContactWarningDialog(true);
      return;
    }

    // No contact info detected, edit normally
    try {
      const result = await editMessage(editingMessageId, editingContent, organizationId);
      
      if (result?.success === false) {
        toast.error(result.error || 'Failed to update message');
      } else {
        setEditingMessageId(null);
        setEditingContent('');
        toast.success('Message updated');
      }
    } catch (err) {
      toast.error('Failed to update message: ' + (err as Error).message);
    }
  };

  const handleEditCancel = () => {
    setEditingMessageId(null);
    setEditingContent('');
  };

  const canEditMessage = (message: JobMessage) => {
    if (!user || message.sender_user_id !== user.id) return false;
    const messageAge = Date.now() - new Date(message.created_at).getTime();
    return messageAge <= 10 * 60 * 1000; // 10 minutes in milliseconds
  };

  const formatMessageTime = (timestamp: string) => {
    const date = new Date(timestamp);
    if (isToday(date)) {
      return `Today ${format(date, 'h:mm a')}`;
    } else if (isYesterday(date)) {
      return `Yesterday ${format(date, 'h:mm a')}`;
    } else {
      return format(date, 'MMM d, h:mm a');
    }
  };

  const getSenderInfo = (message: JobMessage) => {
    const isCurrentUser = message.sender_user_id === user?.id;
    const role = message.profiles?.role || 'user';
    
    if (isCurrentUser) {
      return { name: 'You', role: role === 'client' ? 'Client' : 'Vendor' };
    }
    
    return { 
      name: role === 'client' ? 'Client' : 'Vendor', 
      role: role === 'client' ? 'Client' : 'Vendor' 
    };
  };

  const getDetectionTypeLabel = (type: string) => {
    switch (type) {
      case 'phone_number': return 'phone number';
      case 'email': return 'email address';
      case 'social_media': return 'social media reference';
      case 'url': return 'website URL';
      default: return 'contact information';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <div>Loading messages...</div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-red-600">
          Error loading messages: {error}
        </CardContent>
      </Card>
    );
  }

  if (!isMessagingAvailable) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Messages</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <p>Messaging is only available after the job has been awarded.</p>
            <p className="text-sm mt-2">Current status: <Badge variant="outline">{jobStatus.replace('_', ' ')}</Badge></p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {/* Contact Information Warning Dialog */}
      <AlertDialog open={showContactWarningDialog} onOpenChange={setShowContactWarningDialog}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Off-Platform Communication Notice
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>
                  This message appears to contain {detectedContactInfo ? getDetectionTypeLabel(detectedContactInfo.type) : 'contact information'} that 
                  may be used for communication outside the platform.
                </p>
                
                {detectedContactInfo?.content && (
                  <div className="bg-muted p-3 rounded-md">
                    <p className="text-sm font-medium text-foreground">Detected:</p>
                    <p className="text-sm font-mono text-muted-foreground">{detectedContactInfo.content}</p>
                  </div>
                )}
                
                <p>
                  We encourage all important agreements, decisions, and commitments to be documented 
                  through this messaging system to create a clear record.
                </p>
                
                <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-md border border-amber-200 dark:border-amber-800">
                  <Info className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    <strong>Please note:</strong> Any agreements made outside of this platform cannot be 
                    verified and will not be considered in the event of a dispute.
                  </p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleEditMessage}>
              Edit Message
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleSendAnyway} className="bg-amber-600 hover:bg-amber-700">
              Send Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Card className="h-[600px] flex flex-col">
        <CardHeader className="flex-shrink-0">
          <CardTitle className="flex items-center justify-between">
            <span>Messages</span>
            {unreadCount > 0 && (
              <Badge variant="destructive" className="ml-2">
                {unreadCount} unread
              </Badge>
            )}
          </CardTitle>
        </CardHeader>

        {/* Messages Area */}
        <CardContent className="flex-1 flex flex-col p-0 min-h-0">
          <ScrollArea className="flex-1 px-6 h-full" ref={scrollAreaRef}>
            <div className="space-y-4 py-4">
              {messages.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  <p>No messages yet.</p>
                  <p className="text-sm mt-2">Start the conversation with your {user?.role === 'client' ? 'vendor' : 'client'}.</p>
                </div>
              ) : (
                messages.map((message) => {
                  const senderInfo = getSenderInfo(message);
                  const isCurrentUser = message.sender_user_id === user?.id;
                  const canEdit = canEditMessage(message);

                  return (
                    <div
                      key={message.id}
                      className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] ${isCurrentUser ? 'order-2' : 'order-1'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-muted-foreground">
                            {senderInfo.name}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {senderInfo.role}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {formatMessageTime(message.created_at)}
                          </span>
                          {message.is_edited && (
                            <span className="text-xs text-muted-foreground italic">
                              (edited)
                            </span>
                          )}
                        </div>
                        
                        <div className={`rounded-lg p-3 ${
                          isCurrentUser 
                            ? 'bg-primary text-primary-foreground' 
                            : 'bg-muted'
                        }`}>
                          {editingMessageId === message.id ? (
                            <div className="space-y-2">
                              <Textarea
                                value={editingContent}
                                onChange={(e) => setEditingContent(e.target.value)}
                                className="min-h-[60px] bg-background text-foreground"
                                maxLength={2000}
                              />
                              <div className="flex gap-2">
                                <Button size="sm" onClick={handleEditSave}>
                                  <Check className="h-3 w-3" />
                                </Button>
                                <Button size="sm" variant="outline" onClick={handleEditCancel}>
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="relative group">
                              <p className="whitespace-pre-wrap">{message.message_content}</p>
                              {canEdit && !isReadOnly && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0"
                                  onClick={() => handleEditStart(message)}
                                >
                                  <Edit2 className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          <Separator />

          {/* Message Input */}
          {!isReadOnly && (
            <div className="p-6 flex-shrink-0">
              <div className="space-y-3">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="min-h-[80px] resize-none"
                      maxLength={2000}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                    />
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-xs text-muted-foreground">
                        {newMessage.length}/2000 characters
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Press Enter to send, Shift+Enter for new line
                      </span>
                    </div>
                  </div>
                  <Button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || sending}
                    className="self-end"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {isReadOnly && (
            <div className="p-4 bg-muted/50 text-center text-sm text-muted-foreground">
              This job is completed. No new messages can be sent.
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};
